


















































import { defineComponent, ref, watch, computed, watchEffect, onMounted, Ref, nextTick } from '@vue/composition-api';
import { useWrapperClasses, useInputClasses, useDefaultInputFlags, useExposeFocus } from 'wok-components/src/input/abstractions';
import { generateRandomId } from 'wok-components/src/helpers';

export default defineComponent({
    name: "WokInputTextDir",

    props: {
        name: {
            type: String,
            default: ''
        },
        value: {
            type: String,
            default: ''
        },
        mandatory: {
            type: Boolean,
            default: false
        },
        autoComplete: {
            type: [Array, Function],
            default: undefined
        },
        autoCompleteFmt: {
            type: Function,
            default: undefined
        },
        label: {
            type: String,
            dafault: ''
        },
        error: {
            type: Object,
            default: null
        },
        placeholder: {
            type: String,
            default: ''
        },
        disabled: {
            type: [Boolean, String],
            default: false
        },
        readonly: {
            type: [Boolean, String],
            default: false
        },
        resetValue: {
            type: String,
            default: ''
        },
        showReset: {
            type: Boolean,
            default: false
        },
        actionSequentialFocus: {
            type: Boolean,
            default: false
        },
        focus: {
            type: Boolean,
            default: null
        },
        fmt: {
            type: Function,
            default: undefined
        },
        testName: {
            type: String,
            default: undefined
        },
        dir: {
            type: String,
            default: "ltr"
        }
    },

    model: { prop: 'value', event: 'update:value' },

    setup(props, context) {
        const inputElement = ref() as Ref<HTMLInputElement>;

        const id = generateRandomId(props.name);

        const { isReadOnly, isDisabled } = useDefaultInputFlags(props);

        const { focused, onFocus, onBlur } = useExposeFocus(inputElement, context.emit, props);

        const hasActions = computed(() => props.showReset && !isReadOnly.value && !isDisabled.value);

        const formattedValue = computed(() => props.value || '');

        const formatAutoCompleteItem = (item: any) => props.autoCompleteFmt ? (<any>props.autoCompleteFmt)(item) : item;

        // Auto-complete can be done using an externat list or a function:
        // make sure to abstract over the two methods and cache the resulting
        // list of items.

        const acCached = props.autoComplete instanceof Function ? ref([]) : null;

        const acValues = computed(
            () => acCached != null ? acCached.value : (Array.isArray(props.autoComplete) ? props.autoComplete : null)
        )

        // Keep and index to the "currently selected" item, to enable keyboard navigation.

        const acElementsList = ref<HTMLElement>();

        const acIndex = ref(-1);

        const acIndexStep = (step: number) => {
            let v = acIndex.value + step;
            acIndex.value = (v + acValues.value!.length) % acValues.value!.length;
        }

        // Unfortunately we cannot just depend on the input being focused because a click
        // on the auto-complete list will blur it before anything else and the list
        // itself will go away and we will never receive that click. What we do instead
        // is to keep an extra variable and update it some time after receiving the blur
        // event.

        // Error should be shown only after the input field has been in focus at least once.
        const showError = ref(false);
        
        const acFocused = ref(false);

        const acOnFocus = () => {
            showError.value = false;
            acFocused.value = true;
            onFocus();
        }

        function isTrue(value: string | boolean | null | undefined) {
            return value === 'true' || value === true;
        }

        const acOnBlur = () => {
            showError.value = !isTrue(props.readonly);
            if (props.autoComplete != null) {
                setTimeout(() => acFocused.value = false, 200);
            }
            else {
                acFocused.value = false;
            }
            onBlur();
        }

        const acEnabled = computed(
            () => acValues.value != null && acValues.value.length > 0 && acFocused.value
        )

        const acAutoCompleteUpdate = (hint: string) => {
            // This function is used to update the auto-complete list cached values.
            if (props.autoComplete instanceof Function) {
                let promiseOrResult = props.autoComplete(hint);
                if (promiseOrResult.then) {
                    promiseOrResult.then((x: any) => acCached!.value = x ?? []);
                }
                else {
                    acCached!.value = promiseOrResult ?? [];
                }
            }
            else {
                context.emit('update:autoCompleteHint', hint);
            }
        }

        const acSelectItemByIndex = (index: number, element?: Element) => {
            element = element ?? acElementsList.value?.children[index];
            let item = acValues.value![index];
            if (item != null && element != null) {
                context.emit('update:value', props.fmt ? (<any>props.fmt)(item) : (<HTMLElement>element).textContent?.trim() ?? '');
                context.emit('update:autoCompleteItem', item);
                // Make sure to reset the auto-complete list to a safe (empty) value.
                acAutoCompleteUpdate('');
            }
        }

        const onAutoCompleteItemClick = (evt: Event) => {
            // Find the index of the node in the DOM tree: we use it to recover the correct item.
            if (evt && evt.target instanceof Element) {
                let index = 0;
                let e: Element | null = evt.target;
                while ((e = e.previousElementSibling) != null) { index += 1 }
                if (index >= 0) {
                    acSelectItemByIndex(index, evt.target);
                }
            }
        }

        const onInput = (evt: Event) => {
            if (evt.target instanceof HTMLInputElement) {
                let value = evt.target.value;
                // If the autocomplete is enabled send an update on every input but
                // also process it internally or emit an update:autoCompleteText event.
                if (props.autoComplete != null) {
                    acAutoCompleteUpdate(value);
                }
                context.emit('update:value', props.fmt ? (<any>props.fmt)(value) : value);
            }
        }

        const onKeyDown = (evt: Event) => {
            // If the autocomplete is enabled the keys up, down and return are used
            // to move around and select items in the auto-complete list.
		    if (evt instanceof KeyboardEvent) {
                switch (evt.key)
                {
                    case 'ArrowDown':
                        acIndexStep(+1);
                        evt.preventDefault();
                        break;
                    case 'ArrowUp':
                        acIndexStep(-1);
                        evt.preventDefault();
                        break;
                    case 'Enter':
                        acSelectItemByIndex(acIndex.value);
                        evt.preventDefault();
                        break;
                }
		    }
        }

        const onReset = () => {
            context.emit('update:value', props.resetValue);
            if (acCached != null) {
                acCached.value = [];
            }
        }

        return {
            inputElement,
            id,
            computedTestName: computed(() => props.testName ?? props.name),
            // wrapperClasses : computed(() => useWrapperClasses(props, context.attrs, showError.value)),
            wrapperClasses : computed(() => useWrapperClasses(props, context.attrs)),
            inputClasses : useInputClasses('text', hasActions),
            formattedValue,
            formatAutoCompleteItem,
            hasActions,
            isReadOnly,
            isDisabled,
            acIndex,
            acValues,
            acEnabled,
            acElementsList,
            onInput,
            onKeyDown,
            onReset,
            onFocus: acOnFocus,
            onBlur: acOnBlur,
            onAutoCompleteItemClick
        }
    }
})
